import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import DoctorAnalytics from "@/pages/DoctorAnalytics";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LabelList,
} from "recharts";
import ReportPeriodFilter from "@/components/crm/ReportPeriodFilter";
import { presetRange } from "@/lib/reportPeriods";
import { ROLE_LABELS } from "@/lib/constants";
import RevenueReport from "@/pages/RevenueReport";
import { Building2, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";

// Radix Select can't use an empty-string value, so "All Branches" uses this sentinel.
const ALL_BRANCHES = "all";

// Count labels drawn to the right of each horizontal bar.
const BAR_LABEL = { position: "right", fontSize: 11, fill: "#374151", fontWeight: 600 };
// Adds ~12% headroom past the longest bar so the "right" count label isn't
// clipped at the chart's edge.
const X_DOMAIN = [0, (max) => Math.ceil((max || 0) * 1.12) || 1];

export default function Reports() {
  const { user } = useAuth();
  const [tc, setTc] = useState([]);
  const [fr, setFr] = useState([]);
  const [lost, setLost] = useState([]);
  const [sla, setSla] = useState(null);
  const [callSummary, setCallSummary] = useState(null);
  const [mgmt, setMgmt] = useState(null);
  const [subFreq, setSubFreq] = useState("off");

  // Shared period filter — applies to every sub-tab.
  const [range, setRange] = useState(() => presetRange("this_month"));
  const [activePreset, setActivePreset] = useState("this_month");
  // Shared branch filter — also applies to every sub-tab.
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(ALL_BRANCHES);

  const isAdmin = ["super_admin", "hq_admin"].includes(user?.role);

  useEffect(() => {
    const params = { from: range.from, to: range.to };
    if (selectedBranch !== ALL_BRANCHES) params.branch_id = selectedBranch;
    api.get("/reports/telecaller", { params }).then((r) => setTc(r.data)).catch(() => {});
    api.get("/reports/franchise", { params }).then((r) => setFr(r.data)).catch(() => {});
    api.get("/reports/lost-leads", { params }).then((r) => setLost(r.data)).catch(() => {});
    api.get("/reports/sla", { params }).then((r) => setSla(r.data)).catch(() => {});
    api.get("/reports/call-summary", { params }).then((r) => setCallSummary(r.data)).catch(() => {});
    api.get("/reports/management", { params }).then((r) => setMgmt(r.data)).catch(() => {});
  }, [range.from, range.to, selectedBranch]);

  useEffect(() => {
    api.get("/reports/subscription").then((r) => setSubFreq(r.data.frequency)).catch(() => {});
  }, []);

  // Branch list, narrowed to what this role may actually pick — the backend 403s on an
  // out-of-scope branch, so the options must never offer one.
  useEffect(() => {
    api.get("/branches").then((r) => {
      let list = r.data || [];
      if (user?.role === "branch_manager" && (user?.branch_ids || []).length) {
        list = list.filter((b) => (user.branch_ids || []).includes(b.id));
      } else if (user?.role === "franchisee_owner" && user?.franchise_id) {
        list = list.filter((b) => b.franchise_id === user.franchise_id);
      }
      setBranches(list);
    }).catch(() => {});
  }, [user]);

  // Has the user picked a period yet? Number Views defaults to "today" on first
  // open, but never overrides a period the user chose themselves.
  const periodTouched = useRef(false);
  const applyPreset = (key) => {
    periodTouched.current = true;
    setActivePreset(key);
    setRange(presetRange(key));
  };
  const setCustom = (field, value) => {
    if (!value) return;
    periodTouched.current = true;
    setActivePreset(null);
    setRange((r) => ({ ...r, [field]: value }));
  };
  const onTabChange = (tab) => {
    if (tab === "phone-views" && !periodTouched.current) applyPreset("today");
  };

  const updateSubscription = async (freq) => {
    setSubFreq(freq);
    try {
      await api.put("/reports/subscription", { frequency: freq });
      toast.success(freq === "off" ? "Email summaries disabled" : `Email summaries set to ${freq}`);
    } catch (e) { toast.error("Could not update subscription"); }
  };

  const fmtMins = (m) => {
    if (m == null) return "—";
    if (m < 60) return `${Math.round(m)}m`;
    const h = Math.floor(m / 60);
    const rem = Math.round(m % 60);
    return `${h}h ${rem}m`;
  };

  const fmtSecs = (s) => {
    if (s == null) return "—";
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${String(sec).padStart(2, "0")}s`;
    return `${sec}s`;
  };

  const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

  return (
    <Tabs defaultValue="management" onValueChange={onTabChange} className="space-y-5">
      <TabsList className="h-auto max-w-full flex-wrap justify-start gap-1 bg-white">
        <TabsTrigger value="management" data-testid="report-tab-management">Management</TabsTrigger>
        {isAdmin && <TabsTrigger value="revenue" data-testid="report-tab-revenue">Revenue</TabsTrigger>}
        <TabsTrigger value="telecaller" data-testid="report-tab-telecaller">Tele-caller Productivity</TabsTrigger>
        <TabsTrigger value="franchise" data-testid="report-tab-franchise">Franchise Performance</TabsTrigger>
        <TabsTrigger value="lost" data-testid="report-tab-lost">Lost Lead Analysis</TabsTrigger>
        <TabsTrigger value="sla" data-testid="report-tab-sla">Response Time (SLA)</TabsTrigger>
        <TabsTrigger value="call-summary" data-testid="report-tab-call-summary">Call Summary</TabsTrigger>
        <TabsTrigger value="doctors" data-testid="report-tab-doctors">Doctor Performance</TabsTrigger>
        <TabsTrigger value="phone-views" data-testid="report-tab-phone-views">Number Views</TabsTrigger>
      </TabsList>

      <ReportPeriodFilter
        range={range}
        activePreset={activePreset}
        onPreset={applyPreset}
        onCustom={setCustom}
      />

      {/* Branch filter — applies to every sub-tab, alongside the period above. */}
      {branches.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <Building2 className="h-4 w-4 text-navy/60" />
          <Select value={selectedBranch} onValueChange={setSelectedBranch}>
            <SelectTrigger className="h-8 w-56 border-navy/20 text-sm" data-testid="reports-branch-select">
              <SelectValue placeholder="All Branches" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_BRANCHES}>All Branches</SelectItem>
              {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="flex items-center justify-between rounded-xl border border-navy/10 bg-white p-4 shadow-sm">
        <div>
          <p className="font-display font-semibold text-navy">Email Summary Reports</p>
          <p className="text-sm text-muted-foreground">Get a periodic summary of your leads and follow-ups by email.</p>
        </div>
        <Select value={subFreq} onValueChange={updateSubscription}>
          <SelectTrigger className="w-40" data-testid="report-subscription-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="off">Off</SelectItem>
            <SelectItem value="daily">Daily</SelectItem>
            <SelectItem value="weekly">Weekly</SelectItem>
            <SelectItem value="monthly">Monthly</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <TabsContent value="management" className="space-y-5">
        {!mgmt ? (
          <Card title="Management Reports"><p className="text-sm text-muted-foreground">Loading…</p></Card>
        ) : (
          <ManagementReports
            data={mgmt}
            inr={inr}
            onSaveTarget={async (branch_id, year, month, target) => {
              try {
                await api.put("/reports/targets", { branch_id, year, month, target });
                // Refetch with the SAME filters the table is showing, or saving a
                // target would silently swap the grid back to all branches.
                const refetch = { from: range.from, to: range.to };
                if (selectedBranch !== ALL_BRANCHES) refetch.branch_id = selectedBranch;
                const r = await api.get("/reports/management", { params: refetch });
                setMgmt(r.data);
                toast.success("Target saved");
              } catch (e) { toast.error("Could not save target"); }
            }}
          />
        )}
      </TabsContent>

      {isAdmin && (
        <TabsContent value="revenue">
          <RevenueReport
            from={range.from}
            to={range.to}
            branchId={selectedBranch === ALL_BRANCHES ? "" : selectedBranch}
          />
        </TabsContent>
      )}

      {/* Comparing doctors is a management report, so it lives with the other
          reports. A doctor's OWN numbers are on My Day instead. */}
      <TabsContent value="doctors">
        <DoctorAnalytics embedded />
      </TabsContent>

      <TabsContent value="telecaller" className="space-y-5">
        <Card title="Tele-caller Productivity">
          <Table headers={["Name", "Leads", "Calls", "Talk Time", "Converted", "Conv. %"]}
            rows={tc.map((t) => [t.name, t.leads, t.calls, fmtSecs(t.talk_time_seconds), t.converted, `${t.rate}%`])} empty="No tele-callers" />
        </Card>

        {/* Appointments each tele-caller booked in the period, and what came of
            them. Cohorted by booking date, so it won't tie out against the
            Revenue report — that one counts visits by when they happened. */}
        <Card title="Appointments & Revenue Booked">
          <Table
            headers={["Name", "Booked", "Visited", "No-show", "Show %", "Collected", "Outstanding", "Total"]}
            rows={tc.map((t) => [
              t.name,
              t.appointments_booked ?? 0,
              t.visited ?? 0,
              t.no_show ?? 0,
              `${t.show_rate ?? 0}%`,
              inr(t.revenue_collected),
              inr(t.revenue_outstanding),
              inr(t.revenue_total),
            ])}
            empty="No tele-callers"
          />
        </Card>
      </TabsContent>

      <TabsContent value="franchise">
        <Card title="Franchise Performance">
          <Table headers={["Franchise", "Territory", "Rev. Share", "Leads", "Converted", "Conv. %"]}
            rows={fr.map((f) => [f.name, f.territory || "—", `${f.revenue_share}%`, f.leads, f.converted, `${f.rate}%`])} empty="No franchises" />
        </Card>
      </TabsContent>

      <TabsContent value="lost">
        <Card title="Lost Lead Reasons">
          {lost.length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground">No lost leads</p> : (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={lost} layout="vertical" margin={{ left: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5ddd2" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 12, fill: "#6B7280" }} allowDecimals={false} domain={X_DOMAIN} />
                <YAxis type="category" dataKey="reason" tick={{ fontSize: 12, fill: "#6B7280" }} width={120} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #1B3A5C20" }} />
                <Bar dataKey="count" fill="#C49A6C" radius={[0, 6, 6, 0]}>
                  <LabelList dataKey="count" {...BAR_LABEL} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </TabsContent>
      <TabsContent value="sla" className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-4">
          <KpiCard label="Avg Response Time" value={fmtMins(sla?.overall?.avg_minutes)} />
          <KpiCard label="Median Response Time" value={fmtMins(sla?.overall?.median_minutes)} />
          <KpiCard label="Slowest Response" value={fmtMins(sla?.overall?.max_minutes)} />
          <KpiCard label="Not Yet Contacted" value={sla?.total_leads_uncontacted ?? "—"} />
        </div>
        <Card title="Response Time by Tele-caller">
          <Table headers={["Tele-caller", "Leads Contacted", "Avg", "Median", "Slowest"]}
            rows={(sla?.by_telecaller || []).map((t) => [t.name, t.count, fmtMins(t.avg_minutes), fmtMins(t.median_minutes), fmtMins(t.max_minutes)])}
            empty="No contacted leads yet" />
        </Card>
      </TabsContent>

      <TabsContent value="call-summary" className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-3">
          <KpiCard label="Total Calls" value={callSummary?.totals?.calls ?? "—"} />
          <KpiCard label="Talk Time" value={fmtSecs(callSummary?.totals?.talk_time_seconds)} />
          <KpiCard
            label="Avg Talk Time / Call"
            value={callSummary?.totals?.calls
              ? fmtSecs(Math.round(callSummary.totals.talk_time_seconds / callSummary.totals.calls))
              : "—"}
          />
        </div>
        <Card title="Calls by Tele-caller">
          <Table headers={["Tele-caller", "Calls", "Talk Time"]}
            rows={(callSummary?.by_telecaller || []).map((t) => [t.name, t.calls, fmtSecs(t.talk_time_seconds)])}
            empty="No tele-callers" />
        </Card>
        <Card title="Call Dispositions">
          {(callSummary?.dispositions || []).length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground">No calls logged in this period</p> : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={callSummary.dispositions} layout="vertical" margin={{ left: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5ddd2" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 12, fill: "#6B7280" }} allowDecimals={false} domain={X_DOMAIN} />
                <YAxis type="category" dataKey="disposition" tick={{ fontSize: 12, fill: "#6B7280" }} width={140} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #1B3A5C20" }} />
                <Bar dataKey="count" fill="#1B3A5C" radius={[0, 6, 6, 0]}>
                  <LabelList dataKey="count" {...BAR_LABEL} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </TabsContent>

      {/* Radix unmounts inactive tabs, so this only fetches while it's open. */}
      <TabsContent value="phone-views" className="space-y-5">
        <PhoneViewsReport range={range} branchId={selectedBranch !== ALL_BRANCHES ? selectedBranch : null} />
      </TabsContent>
    </Tabs>
  );
}

// ── Number Views ─────────────────────────────────────────────────────────────
// Who revealed which lead's phone number (POST /leads/:id/phone-view, web + app)
// and whether they acted on it. GET /reports/phone-views.
const ALL_USERS = "all";
const PER_PAGE_OPTIONS = [25, 50, 100];

// Share of views that led to a follow-up or visit being scheduled.
// Conversion = views that led to a follow-up or visit (a call alone doesn't count).
const phoneViewConversion = (views, converted) =>
  views ? `${Math.round(((converted || 0) / views) * 100)}%` : "—";

const fmtIST = (iso, withTime = true) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata", day: "2-digit", month: "short",
    ...(withTime ? { hour: "2-digit", minute: "2-digit" } : { year: "numeric" }),
  });
};

const YesNo = ({ yes, sub }) => (
  <span className="inline-flex flex-col">
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${yes ? "text-emerald-700" : "text-red-600"}`}>
      {yes ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}{yes ? "Yes" : "No"}
    </span>
    {yes && sub && <span className="text-[11px] text-muted-foreground">{sub}</span>}
  </span>
);

function PhoneViewsReport({ range, branchId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState(ALL_USERS);      // server-side filter
  const [focusUser, setFocusUser] = useState(null);     // summary-row click → detail filter
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  useEffect(() => {
    api.get("/users", { params: { include_inactive: true } })
      .then((r) => setUsers([...(r.data || [])].sort((a, b) => (a.name || "").localeCompare(b.name || ""))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const params = { date_from: range.from, date_to: range.to };
    if (userId !== ALL_USERS) params.user_id = userId;
    if (branchId) params.branch_id = branchId;
    setLoading(true);
    api.get("/reports/phone-views", { params })
      .then((r) => setData(r.data))
      .catch((e) => { setData(null); toast.error(formatApiError(e.response?.data?.detail)); })
      .finally(() => setLoading(false));
  }, [range.from, range.to, userId, branchId]);

  const rows = useMemo(
    () => (data?.rows || []).filter((r) => !focusUser || r.user_id === focusUser),
    [data, focusUser],
  );
  const totalPages = Math.max(1, Math.ceil(rows.length / perPage));
  // Back to page 1 whenever the filtered set or page size changes.
  useEffect(() => { setPage(1); }, [data, focusUser, perPage]);
  useEffect(() => { if (page > totalPages) setPage(1); }, [page, totalPages]);
  const pageRows = rows.slice((page - 1) * perPage, page * perPage);

  const totals = data?.totals || {};
  const summary = data?.summary || [];
  const focusName = focusUser && (summary.find((s) => s.user_id === focusUser)?.user_name || "selected user");

  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <Select value={userId} onValueChange={(v) => { setUserId(v); setFocusUser(null); }}>
          <SelectTrigger className="h-8 w-56 border-navy/20 text-sm" data-testid="phone-views-user-select">
            <SelectValue placeholder="All users" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_USERS}>All users</SelectItem>
            {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}{u.role ? ` · ${ROLE_LABELS[u.role] || u.role}` : ""}</SelectItem>)}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          A view counts as called / follow-up / visit if the same user did it within 24 hours of viewing the number.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <KpiCard label="Number Views" value={totals.views ?? "—"} />
        <KpiCard label="Unique Leads" value={totals.unique_leads ?? "—"} />
        <KpiCard label="Called" value={totals.called ?? "—"} />
        <KpiCard label="Follow-up Scheduled" value={totals.followup_scheduled ?? "—"} />
        <KpiCard label="Visit Scheduled" value={totals.visit_scheduled ?? "—"} />
        <KpiCard label="No Action" value={totals.no_action ?? "—"} />
      </div>

      <Card title="Views by User">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-cream/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                {["User", "Role", "Views", "Unique Leads", "Called", "Follow-up", "Visit", "No Action", "Conversion"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {summary.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">{loading ? "Loading…" : "No number views in this period"}</td></tr>
              ) : summary.map((s) => (
                <tr key={s.user_id}
                  onClick={() => setFocusUser((f) => (f === s.user_id ? null : s.user_id))}
                  className={`cursor-pointer border-b last:border-0 ${focusUser === s.user_id ? "bg-nude/40" : "hover:bg-nude/30"}`}
                  data-testid={`phone-views-user-${s.user_id}`}>
                  <td className="px-4 py-3 font-medium text-ink">{s.user_name || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{ROLE_LABELS[s.user_role] || s.user_role || "—"}</td>
                  <td className="px-4 py-3 text-ink">{s.views}</td>
                  <td className="px-4 py-3 text-ink">{s.unique_leads}</td>
                  <td className="px-4 py-3 text-ink">{s.called ?? 0}</td>
                  <td className="px-4 py-3 text-ink">{s.followup_scheduled}</td>
                  <td className="px-4 py-3 text-ink">{s.visit_scheduled}</td>
                  <td className="px-4 py-3 text-ink">{s.no_action}</td>
                  <td className="px-4 py-3 font-semibold text-navy">{phoneViewConversion(s.views, s.converted)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Detail — sticky header, scrolls inside the card, paginated client-side. */}
      <div className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-navy/10 bg-white shadow-sm">
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
          <h3 className="font-display text-lg font-semibold text-navy">View Log</h3>
          {focusUser && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-nude/60 px-2.5 py-1 text-xs font-medium text-navy">
              {focusName}
              <button onClick={() => setFocusUser(null)} aria-label="Clear user filter" className="hover:text-red-600"><X className="h-3 w-3" /></button>
            </span>
          )}
        </div>
        <div className="max-h-[70vh] flex-1 overflow-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10">
              <tr className="border-b bg-cream text-left text-xs uppercase tracking-wider text-muted-foreground whitespace-nowrap">
                {["Time (IST)", "User", "Lead", "Source", "Called", "Follow-up", "Visit"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {pageRows.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">{loading ? "Loading…" : "No number views"}</td></tr>
              ) : pageRows.map((r) => (
                <tr key={r.id} className="border-b last:border-0 hover:bg-nude/20" data-testid="phone-view-row">
                  <td className="whitespace-nowrap px-4 py-2.5 text-muted-foreground">{fmtIST(r.viewed_at)}</td>
                  <td className="px-4 py-2.5">
                    <p className="font-medium text-ink">{r.user_name || "—"}</p>
                    <p className="text-[11px] text-muted-foreground">{ROLE_LABELS[r.user_role] || r.user_role || ""}</p>
                  </td>
                  <td className="px-4 py-2.5">
                    {r.patient_id
                      ? <Link to={`/patients/${r.patient_id}`} className="font-medium text-navy hover:underline">{r.lead_name || "Unnamed patient"}</Link>
                      : r.lead_id
                      ? <Link to={`/leads/${r.lead_id}`} className="font-medium text-navy hover:underline">{r.lead_name || "Unnamed lead"}</Link>
                      : <span className="text-muted-foreground">{r.lead_name || "—"}</span>}
                    {r.patient_id && <p className="text-[11px] text-muted-foreground">Patient page</p>}
                  </td>
                  <td className="px-4 py-2.5 text-xs uppercase text-muted-foreground">{r.source || "—"}</td>
                  <td className="px-4 py-2.5"><YesNo yes={r.called} sub={r.called_at && `At ${fmtIST(r.called_at)}`} /></td>
                  <td className="px-4 py-2.5"><YesNo yes={r.followup_scheduled} sub={r.followup_due_at && `Due ${fmtIST(r.followup_due_at)}`} /></td>
                  <td className="px-4 py-2.5"><YesNo yes={r.visit_scheduled} sub={r.appointment_at && `Appt ${fmtIST(r.appointment_at)}`} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t px-4 py-2.5">
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <span>{rows.length} view{rows.length === 1 ? "" : "s"}</span>
            <div className="flex items-center gap-1.5">
              <span>Per page</span>
              <Select value={String(perPage)} onValueChange={(v) => setPerPage(Number(v))}>
                <SelectTrigger className="h-8 w-[72px] text-xs" data-testid="phone-views-per-page"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PER_PAGE_OPTIONS.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
            <span className="text-sm text-ink">Page {page} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      </div>
    </>
  );
}

const KpiCard = ({ label, value }) => (
  <div className="rounded-xl border border-navy/10 bg-white p-4 shadow-sm">
    <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className="mt-1 font-display text-2xl font-semibold text-navy">{value}</p>
  </div>
);

const Card = ({ title, children }) => (
  <div className="rounded-xl border border-navy/10 bg-white p-6 shadow-sm">
    <h3 className="mb-4 font-display text-lg font-semibold text-navy">{title}</h3>
    {children}
  </div>
);

const Table = ({ headers, rows, empty }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b bg-cream/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
          {headers.map((h) => <th key={h} className="px-4 py-3">{h}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? <tr><td colSpan={headers.length} className="px-4 py-10 text-center text-muted-foreground">{empty}</td></tr> :
          rows.map((r, i) => (
            <tr key={i} className="border-b last:border-0 hover:bg-nude/30">
              {r.map((c, j) => <td key={j} className={`px-4 py-3 ${j === 0 ? "font-semibold text-ink" : "text-muted-foreground"}`}>{c}</td>)}
            </tr>
          ))}
      </tbody>
    </table>
  </div>
);

/* ── Management reports: the ops spreadsheet, per centre × month ──────────── */
// Tint a ratio/percentage cell: red when weak, green when strong (like the Excel
// conditional formatting). Thresholds are deliberate and easy to tweak.
const pctTint = (v) =>
  v >= 35 ? "bg-green-100 text-green-800"
  : v <= 28 ? "bg-red-100 text-red-700"
  : "text-ink";

const thGroup = "border px-3 py-2 text-center text-xs font-semibold text-navy bg-cream/60";
const thSub = "border px-3 py-1.5 text-center text-[11px] font-semibold uppercase tracking-wide text-muted-foreground bg-cream/40";
const td = "border px-3 py-2 text-center text-sm text-ink";

function TargetCell({ row, mm, inr, onSaveTarget }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(String(mm.target || ""));
  if (editing) {
    return (
      <td className={`${td}`}>
        <input
          type="number"
          autoFocus
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onBlur={() => { setEditing(false); const n = parseFloat(val) || 0; if (n !== (mm.target || 0)) onSaveTarget(row.branch_id, mm.year, mm.month, n); }}
          onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") { setVal(String(mm.target || "")); setEditing(false); } }}
          className="w-24 rounded border border-input px-2 py-1 text-right text-sm"
        />
      </td>
    );
  }
  return (
    <td className={`${td} cursor-pointer text-right hover:bg-nude/30`} onClick={() => setEditing(true)} title="Click to set target">
      {mm.target ? inr(mm.target) : <span className="text-muted-foreground">Set</span>}
    </td>
  );
}

function ManagementReports({ data, inr, onSaveTarget }) {
  const months = data.month_labels || [];
  const showRevenue = data.show_revenue;

  return (
    <div className="space-y-6">
      {/* 1. Appointment Bookings vs Visited */}
      <Card title="Appointment Bookings vs Visited">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className={thGroup} rowSpan={2}>Centre</th>
                {months.map((m) => <th key={m} className={thGroup} colSpan={3}>{m}</th>)}
              </tr>
              <tr>
                {months.map((m) => (
                  <React.Fragment key={m}>
                    <th className={thSub}>AB</th><th className={thSub}>Visited</th><th className={thSub}>V/AB</th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data.bookings_vs_visited || []).map((row) => (
                <tr key={row.centre}>
                  <td className={`${td} font-semibold`}>{row.centre}</td>
                  {row.months.map((mm, i) => (
                    <React.Fragment key={i}>
                      <td className={td}>{mm.ab}</td>
                      <td className={td}>{mm.visited}</td>
                      <td className={`${td} font-semibold ${pctTint(mm.ratio)}`}>{mm.ratio}%</td>
                    </React.Fragment>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* 2. Consultation vs Conversion */}
      <Card title="Consultation vs Conversion">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className={thGroup} rowSpan={2}>Centre</th>
                {months.map((m) => <th key={m} className={thGroup} colSpan={4}>{m}</th>)}
              </tr>
              <tr>
                {months.map((m) => (
                  <React.Fragment key={m}>
                    <th className={thSub}>Consult.</th><th className={thSub}>Conv.</th>
                    <th className={thSub}>Conv %</th><th className={thSub}>Not Conv.</th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data.consultation_vs_conversion || []).map((row) => (
                <tr key={row.centre}>
                  <td className={`${td} font-semibold`}>{row.centre}</td>
                  {row.months.map((mm, i) => (
                    <React.Fragment key={i}>
                      <td className={td}>{mm.consultations}</td>
                      <td className={td}>{mm.converted}</td>
                      <td className={`${td} font-semibold ${pctTint(mm.rate)}`}>{mm.rate}%</td>
                      <td className={`${td} ${mm.not_converted > 0 ? "text-red-700 font-semibold" : ""}`}>{mm.not_converted}</td>
                    </React.Fragment>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* 3. Revenue Contribution (admins only) */}
      {showRevenue && data.revenue_contribution && (
        <Card title="Revenue Contribution — by Source / Month">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className={thGroup}>Source</th>
                  {months.map((m) => <th key={m} className={thGroup}>{m}</th>)}
                </tr>
              </thead>
              <tbody>
                {data.revenue_contribution.rows.map((r) => (
                  <tr key={r.source}>
                    <td className={`${td} font-semibold`}>{r.source}</td>
                    {r.months.map((v, i) => <td key={i} className={`${td} text-right`}>{inr(v)}</td>)}
                  </tr>
                ))}
                <tr className="bg-cream/50 font-semibold">
                  <td className={td}>Total</td>
                  {data.revenue_contribution.totals.map((v, i) => <td key={i} className={`${td} text-right`}>{inr(v)}</td>)}
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* 4. Target vs Achieved (admins only) */}
      {showRevenue && data.target_vs_achieved && (
        <Card title="Target vs Achieved">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className={thGroup} rowSpan={2}>Centre</th>
                  {months.map((m) => <th key={m} className={thGroup} colSpan={3}>{m}</th>)}
                </tr>
                <tr>
                  {months.map((m) => (
                    <React.Fragment key={m}>
                      <th className={thSub}>Target</th><th className={thSub}>Achieved</th><th className={thSub}>% Ach.</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.target_vs_achieved.map((row) => (
                  <tr key={row.centre}>
                    <td className={`${td} font-semibold`}>{row.centre}</td>
                    {row.months.map((mm, i) => (
                      <React.Fragment key={i}>
                        <TargetCell row={row} mm={mm} inr={inr} onSaveTarget={onSaveTarget} />
                        <td className={`${td} text-right`}>{inr(mm.achieved)}</td>
                        <td className={`${td} font-semibold ${mm.pct >= 100 ? "bg-green-100 text-green-800" : mm.pct < 60 ? "bg-red-100 text-red-700" : ""}`}>{mm.pct}%</td>
                      </React.Fragment>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">Set monthly targets from the Targets admin (Centre × Month).</p>
        </Card>
      )}
    </div>
  );
}
